/**
 * Runs one tour on one canvas: the step comes from the record's stored
 * progress, the card and the spotlight render in portals, the step's locks go
 * to the presentation store, and advance conditions are observed through
 * public hooks. The record is written only at the end, through
 * `kernel/status.ts`.
 */

import { orgAPI } from "@/api/api";
import {
  useCreateFlowSession,
  useDeleteFlowSession,
} from "@/api/flow_sessions";
import Sentry from "@/integrations/Sentry";
import { useCanvasFloatingChat } from "@/pages/canvas/context/CanvasFloatingChatContext";
import { FLOW_SESSION_MODES, type FlowSession } from "@cohesive/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { useReactFlow } from "@xyflow/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { setPresentation } from "../presentation";
import { useOnboardingStatus } from "../status";
import type { Locks, TourDefinition } from "../types";
import { targetSelector, useAdvance, usePromptSent } from "./advance";
import { dataPatch, donePatch, stepPatch, type TourProgress } from "./progress";
import { STEP_EXIT_MS, TourCard } from "./TourCard";
import { TourSpotlight } from "./TourSpotlight";

const SESSION_KEY = "onboardingSessionId";
const CHAT_TOGGLE_TARGET = "agent-chat-toggle";

// Frames the canvas so the tour's artifacts fit without panning: 0.75 zoom
// at 1920×1080, proportionally less on smaller viewports, floored at 0.45.
const REF_W = 1920;
const REF_H = 1080;
const BASE_ZOOM = 0.75;
const MIN_ZOOM = 0.45;
const ZOOM_APPLY_DELAY_MS = 150;

export function TourRunner({
  tour,
  canvasId,
  progress,
}: {
  tour: TourDefinition;
  canvasId: string;
  progress: TourProgress;
}) {
  const status = useOnboardingStatus();
  const queryClient = useQueryClient();
  const reactFlow = useReactFlow();
  const { openSession, closeChat, currentSessionId } = useCanvasFloatingChat();
  const createFlowSession = useCreateFlowSession();
  const deleteFlowSession = useDeleteFlowSession();

  const total = tour.steps.length;
  const stepIndex = Math.min(Math.max(1, progress.step), total);
  const step = tour.steps[stepIndex - 1];
  const contentKey = `${stepIndex}`;

  const [outgoing, setOutgoing] = useState(false);
  const [emphasisTick, setEmphasisTick] = useState(0);
  const [dynamicLocks, setDynamicLocks] = useState<Locks>({});
  useEffect(() => setDynamicLocks({}), [contentKey]);

  const data = progress.data;
  const setData = useCallback(
    (patch: Record<string, string>) => {
      void status.patch(dataPatch(tour.id, patch));
    },
    [status, tour.id],
  );

  // ── Tour chat session (mode canvas_onboarding) ────────────────────────
  const sessionId = data[SESSION_KEY] ?? null;
  const sessionIdRef = useRef<string | null>(sessionId);
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);
  const inFlightRef = useRef<Promise<void> | null>(null);
  const openTourChat = useCallback((): Promise<void> => {
    if (inFlightRef.current) return inFlightRef.current;
    const run = async () => {
      const existing = sessionIdRef.current;
      if (existing) {
        let sessions = queryClient.getQueryData<FlowSession[]>([
          "canvas-flow-sessions",
          canvasId,
        ]);
        if (!sessions) {
          try {
            sessions = await orgAPI.canvas.getFlowSessions(canvasId);
          } catch {
            sessions = undefined;
          }
        }
        if (sessions?.some((s) => s.id === existing)) {
          openSession(existing, "replace");
          return;
        }
      }
      try {
        // No harness: the tour is scripted and its composer is locked, so
        // there is no selector to read. It runs on cohesive, as written.
        const session = await createFlowSession.mutateAsync({
          mode: FLOW_SESSION_MODES.CANVAS_ONBOARDING,
          class: "canvas",
          canvas_id: canvasId,
        });
        sessionIdRef.current = session.id;
        setData({ [SESSION_KEY]: session.id });
        openSession(session.id, "replace");
      } catch (err) {
        Sentry.captureException(
          err instanceof Error ? err : new Error("Failed to open tour chat"),
        );
        toast.error("Failed to open chat");
      }
    };
    const promise = run().finally(() => {
      inFlightRef.current = null;
    });
    inFlightRef.current = promise;
    return promise;
  }, [canvasId, createFlowSession, openSession, queryClient, setData]);

  const deleteTourChat = useCallback(async () => {
    const existing = sessionIdRef.current;
    closeChat();
    if (!existing) return;
    sessionIdRef.current = null;
    try {
      await deleteFlowSession.mutateAsync(existing);
    } catch (err) {
      // Best effort: an already-deleted session must not block the finish.
      Sentry.captureException(
        err instanceof Error ? err : new Error("Failed to delete tour chat"),
      );
    }
    void queryClient.invalidateQueries({
      queryKey: ["canvas-flow-sessions", canvasId],
    });
    queryClient.removeQueries({ queryKey: ["flowSession", existing] });
  }, [canvasId, closeChat, deleteFlowSession, queryClient]);

  const tourChatOpen = !!sessionId && currentSessionId === sessionId;

  // ── Presentation ───────────────────────────────────────────────────────
  const locks: Locks = useMemo(
    () => ({ ...tour.locks, ...step.locks, ...dynamicLocks }),
    [tour.locks, step.locks, dynamicLocks],
  );
  // Null while a step is outgoing: readers that key on the target (the
  // publish popover) close between two steps that spotlight the same one.
  const spotlightTarget = outgoing ? null : (locks.spotlightTarget ?? null);
  const viewportLocked = !!locks.viewportLocked;
  const canvasChatHidden =
    spotlightTarget === CHAT_TOGGLE_TARGET ? false : !!locks.canvasChatHidden;
  const composerPinnedPrompt = locks.composerPinnedPrompt ?? null;
  const suggestionsHidden = !!locks.suggestionsHidden;
  useEffect(
    () =>
      setPresentation({
        viewportLocked,
        spotlightTarget,
        canvasChatHidden,
        composerPinnedPrompt,
        suggestionsHidden,
      }),
    [
      viewportLocked,
      spotlightTarget,
      canvasChatHidden,
      composerPinnedPrompt,
      suggestionsHidden,
    ],
  );

  // The spotlit chat launcher opens the tour's own session instead of the
  // canvas agent the launcher would otherwise mint.
  useEffect(() => {
    if (spotlightTarget !== CHAT_TOGGLE_TARGET) return;
    const selector = targetSelector(CHAT_TOGGLE_TARGET);
    const handler = (e: Event) => {
      const el = e.target as Element | null;
      if (!el?.closest?.(selector)) return;
      e.preventDefault();
      e.stopPropagation();
      void openTourChat();
    };
    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
  }, [spotlightTarget, openTourChat]);

  // ── Transitions ────────────────────────────────────────────────────────
  const transitioningRef = useRef(false);

  const finish = useCallback(() => {
    if (transitioningRef.current) return;
    transitioningRef.current = true;
    void status.finish();
    void deleteTourChat();
    setOutgoing(true);
    window.setTimeout(() => {
      void status.patch(donePatch(tour.id));
    }, STEP_EXIT_MS);
  }, [deleteTourChat, status, tour.id]);

  const advance = useCallback(() => {
    if (transitioningRef.current) return;
    if (stepIndex >= total) {
      finish();
      return;
    }
    transitioningRef.current = true;
    setOutgoing(true);
    window.setTimeout(() => {
      void status.patch(stepPatch(tour.id, stepIndex + 1));
      transitioningRef.current = false;
      setOutgoing(false);
    }, STEP_EXIT_MS);
  }, [finish, status, stepIndex, total, tour.id]);

  const promptSent = usePromptSent(
    sessionId,
    step.advance.kind === "prompt-sent" ? step.advance.prompt : null,
  );
  useAdvance({
    condition: step.advance,
    stepKey: contentKey,
    promptSent,
    active: !outgoing,
    onAdvance: advance,
  });

  // One-shot framing on mount; later pan and zoom are the user's.
  useEffect(() => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const scale = Math.min(1, w / REF_W, h / REF_H);
    const zoom = Math.max(MIN_ZOOM, BASE_ZOOM * scale);
    const t = window.setTimeout(() => {
      reactFlow.setViewport({
        x: Math.round(w * 0.25),
        y: Math.round(h * 0.5),
        zoom,
      });
    }, ZOOM_APPLY_DELAY_MS);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setLocks = useCallback(
    (patch: Locks) => setDynamicLocks((prev) => ({ ...prev, ...patch })),
    [],
  );
  const emphasizeSpotlight = useCallback(
    () => setEmphasisTick((t) => t + 1),
    [],
  );
  const openTourChatVoid = useCallback(() => {
    void openTourChat();
  }, [openTourChat]);

  const Step = step.component;
  return (
    <>
      <TourSpotlight target={spotlightTarget} emphasisTick={emphasisTick} />
      <TourCard
        anchor={step.anchor}
        contentKey={contentKey}
        outgoing={outgoing}
      >
        <Step
          data={data}
          setData={setData}
          onAdvance={advance}
          onFinish={finish}
          setLocks={setLocks}
          emphasizeSpotlight={emphasizeSpotlight}
          openTourChat={openTourChatVoid}
          tourChatOpen={tourChatOpen}
          promptSent={promptSent}
        />
      </TourCard>
    </>
  );
}
